import {Component, Input, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {MatDialogRef} from '@angular/material/dialog';
import {Observable, map, startWith} from 'rxjs';
import {PlaygroundLine, PlaygroundProductType, PlaygroundLookups, PlaygroundVendor} from '../../model/playground.model';

@Component({
  selector: 'playground-line-dialog',
  templateUrl: './playground-line-dialog.component.html',
  standalone: false
})
export class PlaygroundLineDialogComponent implements OnInit {
  @Input() salesOrderId!: number;
  @Input() productTypes: PlaygroundProductType[] = [];
  @Input() editData: PlaygroundLine | null = null;
  @Input() agentCommissionAmount: number | null = null;
  @Input() lookups: PlaygroundLookups | null = null;

  form!: FormGroup;
  isEdit = false;
  filteredVendors!: Observable<PlaygroundVendor[]>;
  filteredSubVendors!: Observable<PlaygroundVendor[]>;
  selectedTravelerControl = new FormControl('');
  travelersList: string[] = [];

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<PlaygroundLineDialogComponent>
  ) {}

  ngOnInit(): void {
    this.isEdit = !!this.editData;
    this.form = this.fb.group({
      productTypeId: [this.editData?.productTypeId || null],
      serviceName: [this.editData?.serviceName || ''],
      vendorId: [this.editData?.vendorId || null],
      vendorName: [this.editData?.vendorName || ''],
      subVendorId: [this.editData?.subVendorId || null],
      subVendorName: [this.editData?.subVendorName || ''],
      cost: [this.editData?.actualCost || null],
      price: [this.editData?.quotedCost || null],
      agentCommission: [this.agentCommissionAmount || null],
      currencyCode: [this.editData?.currencyCode || 'USD'],
      serviceStartDate: [this.editData?.serviceStartDate ? new Date(this.editData.serviceStartDate) : null],
      serviceEndDate: [this.editData?.serviceEndDate ? new Date(this.editData.serviceEndDate) : null],
      totalUnits: [this.editData?.totalUnits || 1],
      travelers: [this.editData?.travelers || ''],
      vendorReference: [this.editData?.vendorReference || '']
    });

    if (this.editData?.travelers) {
      this.travelersList = this.editData.travelers.split(',').map(t => t.trim()).filter(t => t);
    }

    this.filteredVendors = this.form.get('vendorName')!.valueChanges.pipe(
      startWith(this.editData?.vendorName || ''),
      map(value => this._filterVendors(value, this.lookups?.vendors || []))
    );

    this.filteredSubVendors = this.form.get('subVendorName')!.valueChanges.pipe(
      startWith(this.editData?.subVendorName || ''),
      map(value => this._filterVendors(value, this.lookups?.subVendors || []))
    );
  }

  private _filterVendors(value: string, options: PlaygroundVendor[]): PlaygroundVendor[] {
    const filter = (value || '').toLowerCase();
    return options.filter(o => o.name.toLowerCase().includes(filter));
  }

  onVendorSelected(name: string): void {
    const vendor = this.lookups?.vendors.find(v => v.name === name);
    this.form.patchValue({vendorId: vendor?.id || null});
  }

  onSubVendorSelected(name: string): void {
    const vendor = this.lookups?.subVendors.find(v => v.name === name);
    this.form.patchValue({subVendorId: vendor?.id || null});
  }

  addTraveler(): void {
    const name = this.selectedTravelerControl.value;
    if (name && !this.travelersList.includes(name)) {
      this.travelersList.push(name);
      this.form.patchValue({travelers: this.travelersList.join(', ')});
    }
    this.selectedTravelerControl.setValue('');
  }

  removeTraveler(name: string): void {
    this.travelersList = this.travelersList.filter(t => t !== name);
    this.form.patchValue({travelers: this.travelersList.join(', ')});
  }

  save(): void {
    if (this.form.valid) {
      const {cost, price, agentCommission, ...rest} = this.form.value;
      this.dialogRef.close({
        salesOrderId: this.salesOrderId,
        ...rest,
        actualCost: cost,
        quotedCost: price,
        agentCommission: agentCommission || null,
        serviceStartDate: rest.serviceStartDate instanceof Date ? rest.serviceStartDate.toISOString().substring(0, 10) : rest.serviceStartDate || null,
        serviceEndDate: rest.serviceEndDate instanceof Date ? rest.serviceEndDate.toISOString().substring(0, 10) : rest.serviceEndDate || null
      });
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
